/** Turns the diagnostics on for this browser until it is cleared. */
const DEBUG_KEY = 'ap-passkey-debug';

/** `?passkeyDebug=1` to arm, `?passkeyDebug=0` to disarm. */
const DEBUG_PARAM = 'passkeyDebug';

/**
 * Whether to narrate suppressions in this browser.
 *
 * Development always narrates. Elsewhere it takes a deliberate switch, latched
 * in storage rather than read from the URL each time: every passkey journey
 * crosses at least one redirect or reload, and a query param would be gone by
 * the step whose silence is being investigated.
 */
const debugSwitchArmed = (): boolean => {
    if (typeof window === 'undefined') return false;

    try {
        const param = new URLSearchParams(window.location.search).get(
            DEBUG_PARAM,
        );
        if (param === '1') {
            window.localStorage.setItem(DEBUG_KEY, '1');
            return true;
        }
        if (param === '0') {
            window.localStorage.removeItem(DEBUG_KEY);
            return false;
        }

        return window.localStorage.getItem(DEBUG_KEY) === '1';
    } catch {
        // Private mode, storage disabled — no diagnostics, never a throw.
        return false;
    }
};

/** Narrate to the console: always in development, elsewhere on request. */
const debugEnabled = (): boolean => import.meta.env.DEV || debugSwitchArmed();

/**
 * Every passkey path fails open: no offer, no step 0, no error. That is right
 * for readers and useless for anyone working on it, because a suppressed path
 * and a broken one look identical — and on a deployed environment, where this
 * is most often investigated, there is nothing in the server logs either
 * because nothing failed. Each suppression says why, on request.
 *
 * Arm it on the device with `?passkeyDebug=1`, then reproduce.
 */
export const logPasskeySuppressed = (area: string, reason: string): void => {
    if (!debugEnabled()) return;
    // eslint-disable-next-line no-console
    console.info(`[passkey] ${area} not shown: ${reason}.`);
};

/**
 * The underlying WebAuthn failure, for showing on screen while debugging.
 *
 * These are all `DOMException`s whose name is the whole diagnosis —
 * `InvalidStateError` (already enrolled), `SecurityError` (relying party id is
 * not a suffix of this origin), `NotSupportedError`. Returns null when the
 * switch is off, so the reader keeps the plain line.
 *
 * Gated on the switch alone, not on development: this one is rendered on
 * screen, and what a reader sees should not quietly differ between builds.
 */
export const passkeyDebugDetail = (err: unknown): string | null => {
    if (!debugSwitchArmed()) return null;
    if (!(err instanceof Error)) return `Passkey debug: ${String(err)}`;

    return `Passkey debug: ${err.name}${err.message ? ` — ${err.message}` : ''}`;
};
